import React, { useEffect, useMemo, useState } from 'react';
import { Card, Typography, Button, Divider, Checkbox, Space } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.bubble.css';
import ReactMarkdown from 'react-markdown';
import rehypeHighlight from 'rehype-highlight';
import hljs from 'highlight.js/lib/core';
import 'highlight.js/styles/atom-one-light.css';
import javascript from 'highlight.js/lib/languages/javascript';
import python from 'highlight.js/lib/languages/python';
import c from 'highlight.js/lib/languages/c';
import cpp from 'highlight.js/lib/languages/cpp';
import java from 'highlight.js/lib/languages/java';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

import styles from './McqCard.scss';
import McqOptions from './McqOptions';
import * as actions from '../../store/actions';
import analytics from '../../services/analytics';
import { setQuestionDetails } from '../../store/actions/McqActions';
import { updateSectionRoute } from '../../Utils/sectionUtils';
import { updateQuestionDetails } from '../../Utils/questionDetailsUpdater';

const { Text } = Typography;
hljs.registerLanguage('javascript', javascript);
hljs.registerLanguage('python', python);
hljs.registerLanguage('c', c);
hljs.registerLanguage('cpp', cpp);
hljs.registerLanguage('java', java);
hljs.configure({ ignoreUnescapedHTML: true });

const McqCard = props => {
  const { t } = useTranslation('common');

  const {
    nextAndPreviousButtonClick,
    currentMcqId,
    updateQuestiondetails,
    updateAnswer,
    isSaving,
    isSidePanelOpen,
    isLastInSection,
  } = props;

  const [isSavingMarkForReview, setIsSavingMarkForReview] = useState(false);

  const modules = useMemo(
    () => ({
      syntax: {
        highlight: text => hljs.highlightAuto(text).value,
      },
    }),
    [],
  );

  const { currentMcq, questionDetails, progress } = useSelector(state => state.mcq);
  const { user_id } = useSelector(state => state.auth);
  const { assessment_id } = useSelector(state => state.invite);
  const { sections, currentSectionId, mcqsResponses } = useSelector(state => state.assessment);
  const switchableSections = useSelector(state => state.assessment.switchableSections);

  const loading = !currentMcq || !currentMcq.id;

  const dispatch = useDispatch();
  const history = useHistory();

  const DEFAULT_RESPONSE_DETAILS = {
    id: '',
    visited: false,
    answered: false,
    markedForReview: false,
    answer: [],
  };

  const currentSectionIndex = sections
    ? sections.findIndex(section => section.id === currentSectionId)
    : -1;

  const isLastSection =
    sections && sections.length > 0 && currentSectionIndex === sections.length - 1;

  const createHandleMcqSidePanelClick = offset => () => {
    const section = sections[currentSectionIndex + offset];
    const mcqIds = section.mcqs.map(mcqId => ({ id: mcqId }));
    const newQuestionDetails = updateQuestionDetails(
      mcqIds,
      mcqsResponses,
      DEFAULT_RESPONSE_DETAILS,
    );
    dispatch(setQuestionDetails(newQuestionDetails));
    dispatch(actions.setProgress({ ...progress, total: mcqIds.length }));
    dispatch(actions.getRecoredResponses(assessment_id));
  };

  const handleMcqSidePanelClick = createHandleMcqSidePanelClick(1);
  const handleMcqSidePanelClickPrev = createHandleMcqSidePanelClick(-1);

  const currentIndex = props.currentMcqIds
    ? props.currentMcqIds.findIndex(mcq => mcq.id === currentMcqId)
    : -1;

  const questionDetail =
    questionDetails && currentIndex !== -1 ? questionDetails[currentIndex] : null;

  const handleMarkForReview = async e => {
    if (!questionDetail) return;

    const isChecked = e.target.checked;
    const wasChecked = questionDetail.markedForReview;

    if (isChecked === wasChecked) return;

    setIsSavingMarkForReview(true);

    try {
      if (isChecked) {
        await dispatch(actions.updateMcqMarkedForReview(currentMcq.id, true));
        updateQuestiondetails(currentIndex, 'addToMarkedForReview');
        try {
          analytics.trackMcqMarkedForReview(currentMcq.id, true);
        } catch (er) {}
      } else {
        await dispatch(actions.updateMcqMarkedForReview(currentMcq.id, false));
        updateQuestiondetails(currentIndex, 'removeFromMarkedForReview');
        try {
          analytics.trackMcqMarkedForReview(currentMcq.id, false);
        } catch (er) {}
      }
    } catch (error) {
      if (isChecked) {
        updateQuestiondetails(currentIndex, 'removeFromMarkedForReview');
      } else {
        updateQuestiondetails(currentIndex, 'addToMarkedForReview');
      }
    } finally {
      setIsSavingMarkForReview(false);
    }
  };

  const handleNext = async () => {
    if (currentIndex < props.currentMcqIds.length - 1) {
      try {
        const newMcqId = props.currentMcqIds[currentIndex + 1].id;
        analytics.trackMcqNavigated('next', currentMcqId, newMcqId);
      } catch (e) {}
      nextAndPreviousButtonClick(currentMcqId, 'next');
    } else {
      const nextSection = sections[currentSectionIndex + 1];
      await updateSectionRoute({
        sections,
        assessmentId: assessment_id,
        sectionId: nextSection.id,
        history,
        handleSidePanelClick: null,
        handleMcqSidePanelClick,
      });
    }
  };

  const handlePrevious = async () => {
    if (currentIndex > 0) {
      try {
        const newMcqId = props.currentMcqIds[currentIndex - 1].id;
        analytics.trackMcqNavigated('previous', currentMcqId, newMcqId);
      } catch (e) {}
      nextAndPreviousButtonClick(currentMcqId, 'previous');
    } else if (switchableSections && currentSectionIndex > 0) {
      const prevSection = sections[currentSectionIndex - 1];
      await updateSectionRoute({
        sections,
        assessmentId: assessment_id,
        sectionId: prevSection.id,
        history,
        handleSidePanelClick: null,
        handleMcqSidePanelClick: handleMcqSidePanelClickPrev,
        isPrevious: true,
      });
    }
  };

  useEffect(() => {
    if (user_id && assessment_id && currentMcqId) {
      dispatch(actions.updateMcqSeen(currentMcqId));
    }
  }, [user_id, assessment_id, currentMcqId]);

  useEffect(() => {
    if (questionDetail && currentIndex !== -1) {
      updateQuestiondetails(currentIndex, 'visited');
    }
  }, [questionDetail, currentIndex]);

  return (
    <Card
      style={{
        width: '1220px',
        margin: isSidePanelOpen ? '10px auto 75px 10em' : '10px auto 75px 17.85em',
        padding: '14px 9px 25px 32px',
        minHeight: '366px',
        height: 'fit-content',
        textAlign: 'left',
      }}
    >
      {loading ? (
        <>
          <div className={styles['question-header']}>
            <Skeleton
              width={150}
              height={30}
            />
            <Skeleton
              width={80}
              height={20}
              style={{ marginLeft: 'auto' }}
            />
          </div>
          <Skeleton height={100} />
          <Skeleton
            height={50}
            count={4}
            style={{ marginTop: 16 }}
          />
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              marginTop: 24,
            }}
          >
            <div className={styles['button-space']}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  position: 'relative',
                  left: '0.4%',
                }}
              >
                <Skeleton
                  width={120}
                  height={20}
                />
              </div>
              <div style={{ display: 'flex', gap: 20, position: 'relative', right: '0.5%' }}>
                <Skeleton
                  width={86}
                  height={38}
                />
                <Skeleton
                  width={61}
                  height={38}
                />
              </div>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className={styles['question-header']}>
            <Text
              type="primary"
              style={{ fontSize: '22px' }}
            >
              {t('mcq.question_label')} {currentIndex + 1}{' '}
            </Text>
            <Text type="primary">
              {currentMcq.point} {t('mcq.point', { count: currentMcq.point })}
            </Text>
          </div>

          <Divider className={styles['divider']} />

          {currentMcq.format === 'html' ? (
            <ReactQuill
              className={styles['quill-editor']}
              theme="bubble"
              readOnly={true}
              tabIndex={-1}
              modules={modules}
              value={currentMcq.text}
            />
          ) : (
            <ReactMarkdown
              className={styles['markdown-editor']}
              rehypePlugins={[rehypeHighlight]}
            >
              {currentMcq.text}
            </ReactMarkdown>
          )}

          <McqOptions
            currentMcq={currentMcq}
            questionDetail={questionDetail}
            updateAnswer={updateAnswer}
            currentIndex={currentIndex}
          />

          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              marginTop: 24,
            }}
          >
            <div className={styles['button-space']}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  position: 'relative',
                  left: '0.4%',
                }}
              >
                <Checkbox
                  checked={questionDetail?.markedForReview || false}
                  onChange={handleMarkForReview}
                  className={styles['mark-for-review-checkbox']}
                >
                  <span style={{ fontSize: '10.4px' }}>{t('mcq.mark_for_review')}</span>
                </Checkbox>

                {isSavingMarkForReview && (
                  <span
                    style={{
                      marginLeft: 8,
                      color: '#52c41a',
                      fontSize: '12px',
                      width: 0,
                      height: 0,
                      position: 'relative',
                      left: '60px',
                      bottom: '6px',
                    }}
                  >
                    {t('mcq.saving')}
                  </span>
                )}
              </div>

              <Space
                size={20}
                style={{ position: 'relative', right: '0.5%' }}
              >
                {(currentIndex > 0 || (switchableSections && currentSectionIndex > 0)) &&
                !(currentIndex === 0 && currentSectionIndex === 0) ? (
                  <Button
                    disabled={isSaving}
                    onClick={handlePrevious}
                    style={{
                      borderRadius: '2px',
                      width: '86px',
                      height: '38px',
                      borderColor: 'var(--selector-marker)',
                      color: 'var(--selector-marker)',
                    }}
                  >
                    {t('mcq.previous')}
                  </Button>
                ) : null}

                {(!isLastInSection || (switchableSections && !isLastSection)) &&
                !(isLastInSection && isLastSection) ? (
                  <Button
                    onClick={handleNext}
                    type="primary"
                    disabled={isSaving}
                    style={{
                      borderRadius: 0,
                      width: '61px',
                      height: '38px',
                    }}
                  >
                    {t('mcq.next')}
                  </Button>
                ) : null}
              </Space>
            </div>
          </div>
        </>
      )}
    </Card>
  );
};

export default McqCard;
